import { useTranslation } from 'react-i18next';
import { useNavigationContext, useOrganisation, useProvider } from '~/Hooks';
import { IOrganisation, IOrganisationSettings } from '~/Models/DTO/Organisation.model';
import { IItemCreationSectionInjectedProps } from '~/ModularComponents';
import { OrganisationSettingsValidation } from './OrganisationSettingsValidation';
import { settingsDetailVerification } from './verification';
import { useMutateOrganisationSettings, useRequestOrganisationSettings } from '~/requests';
import { useEffect, useState } from 'react';

const _initialValues: IOrganisation & IOrganisationSettings = {
  logoUrl: '',
  logoOriginalUrl: '',
  id: '',
  name: '',
  email: '',
  phone: '',
  domain: '',
  isStaffNameVisible: false,
};

export const useOrganisationSettings = () => {
  const { t } = useTranslation();
  const { blockNavigation } = useNavigationContext();
  const {
    organisation,
    updateOrganisation,
    isOrganisationLoading,
    isOrganisationUpdating,
    organisationError,
  } = useOrganisation();
  const { currentProvider } = useProvider();
  const organisationSettings = useRequestOrganisationSettings(currentProvider?.organisationId);
  const updateOrganisationSettings = useMutateOrganisationSettings(currentProvider?.organisationId);

  const [initialValues, setInitialValues] = useState<IOrganisation & IOrganisationSettings>();

  const onSave = (
    store: IItemCreationSectionInjectedProps<IOrganisation & IOrganisationSettings>,
  ) => {
    const payload = store.values;
    updateOrganisationSettings({ isStaffNameVisible: payload.isStaffNameVisible });
    updateOrganisation(payload);
    blockNavigation(false);
  };

  const validate = (data: IOrganisation) => settingsDetailVerification(data, t);

  useEffect(() => {
    if (!initialValues && organisationSettings.data) {
      setInitialValues({
        ...organisation,
        isStaffNameVisible: organisationSettings.data?.isStaffNameVisible || false,
      });
    }
  }, [organisation, organisationSettings.data]);

  return {
    initialValues: initialValues || _initialValues,
    onSave,
    validate,
    currentOrganisation: organisation,
    error: organisationError,
    isLoading: isOrganisationLoading || isOrganisationUpdating,
    validationSchema: OrganisationSettingsValidation(t),
  };
};
